import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pagination, Rate } from "antd";
import moment from "moment";
import ReactDragListView from "react-drag-listview";

import { FaLocationDot, FaCircleMinus, FaCirclePlus } from "react-icons/fa6";
import { FaDollarSign, FaTrashAlt } from "react-icons/fa";

import {
  SortButton,
  Buttons,
  Spacer,
  popover,
  boxContainer,
  inputFields,
  tables,
  checkbox,
  GetProPopup,
} from "../../components";

import { Utils, UtilityArrays, Images } from "../../config";

import {
  OthersAction,
  WatchlistAction,
  SearchAction,
} from "../../store/actions";

import FilterButtons from "../universities/filterbuttons";

import "./styles.css";

const columns = [
  {
    title: "University Name",
    dataIndex: "name",
    key: "1",
    fixed: "left",
    width: "31.25em",
    render: (text, record) => (
      <div className="flex gap-2">
        <div className="w-[15%]">
          <img
            src={record?.profilePic ?? Images.LogoIconOnly}
            className="h-[3.75em] w-[3.75em] border border-searchBorder shadow-boxContainer rounded-full object-contain bg-white"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = Images.LogoIconOnly;
            }}
          />
        </div>

        <div className="flex flex-col gap-2 w-[85%]">
          <div className="font-bold">{record.name}</div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <FaLocationDot />
            <div>
              {`${record?.city ? record?.city : ""}${
                record?.state ? `, ${record?.state}` : ""
              }${record?.country?.name ? `, ${record?.country.name}` : ""}`}
            </div>
          </div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <div className="pr-2 border-r border-r-gray-400">
              Average Fee:{" "}
              {record?.avgFeesUSD ? (
                <popover.WhitePopover
                  content={
                    <div>
                      {record?.avgFeesUSD
                        ? `USD ${record?.avgFeesUSD?.toLocaleString()}`
                        : "NA"}
                    </div>
                  }
                  placement={"top"}
                  trigger="hover"
                >
                  <Rate
                    defaultValue={
                      record?.avgFeesUSD > 0 && record?.avgFeesUSD <= 10000
                        ? 1
                        : record?.avgFeesUSD > 10000 &&
                          record?.avgFeesUSD <= 25000
                        ? 2
                        : record?.avgFeesUSD > 25000 &&
                          record?.avgFeesUSD <= 40000
                        ? 3
                        : record?.avgFeesUSD > 40000 &&
                          record?.avgFeesUSD <= 60000
                        ? 4
                        : 5
                    }
                    character={({ index = 0 }) => customIcons[index + 1]}
                    className="text-main text-[1.25em]"
                    disabled
                  />
                </popover.WhitePopover>
              ) : (
                "NA"
              )}
            </div>
            <div>
              Acceptance Rate:{" "}
              {record?.acceptanceRate
                ? `${(record?.acceptanceRate * 100).toFixed(1)}%`
                : "NA"}
            </div>
          </div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <div className="pr-2 border-r border-r-gray-400">
              Average Course Duration: {record.courseDuration}{" "}
              {record?.courseDuration
                ? record?.courseDuration?.includes("Years")
                  ? null
                  : "Years"
                : "NA"}
            </div>
            <div>
              Application Status:{" "}
              {record?.applicationStatus === "NA" ? (
                "NA"
              ) : (
                <span
                  className={`${
                    record?.applicationStatus === "closed"
                      ? "text-red-500"
                      : "text-green-500"
                  }  font-bold`}
                >
                  {record?.applicationStatus === "closed" ? "CLOSED" : "OPEN"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "Application Deadline",
    dataIndex: "appDeadline",
    key: "10",
    width: 180,
    align: "center",
    render: (text, record) => (
      <div className="flex flex-col">
        <div className="font-bold rounded-md bg-liteBoxBg border border-main">
          {record?.deadlines?.length > 0
            ? record?.deadlines[0]?.appDeadline
              ? moment(record?.deadlines[0]?.appDeadline).format(
                  "DD MMMM, YYYY"
                )
              : "NA"
            : "NA"}
        </div>
      </div>
    ),
  },
  {
    title: "QS World Ranking",
    dataIndex: "qs",
    key: "2",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.rankings?.QS ? `#${record?.rankings?.QS}` : "NA"}</div>
    ),
  },
  {
    title: "Average Fee",
    dataIndex: "avgFee",
    key: "4",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.avgFeesUSD
          ? `USD ${record?.avgFeesUSD.toLocaleString()}`
          : "NA"}
      </div>
    ),
  },
  {
    title: "University Type",
    dataIndex: "type",
    key: "5",
    width: 170,
    align: "center",
    render: (text, record) => <div>{record?.Type?.type ?? "NA"}</div>,
  },
  {
    title: "ACT Midrange",
    dataIndex: "actmirange",
    key: "6",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.ACTMidrange ?? "NA"}</div>
    ),
  },
  {
    title: "SAT Midrange",
    dataIndex: "satmirange",
    key: "7",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATMidrange ?? "NA"}</div>
    ),
  },

  {
    title: "IELTS Requirement",
    dataIndex: "ielts",
    key: "8",
    width: 180,
    align: "center",
    render: (text, record) => <div>{record?.requirements?.IELTS ?? "NA"}</div>,
  },
  {
    title: "TOEFL Requirement",
    dataIndex: "toefl",
    key: "9",
    width: 180,
    align: "center",
    render: (text, record) => <div>{record?.requirements?.TOEFL ?? "NA"}</div>,
  },

  {
    title: (
      <>
        SAT 25<sup>th</sup> Percentile
      </>
    ),
    dataIndex: "sat25",
    key: "11",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATEnglish25Percentile ?? "NA"}</div>
    ),
  },

  {
    title: (
      <>
        SAT 75<sup>th</sup> Percentile
      </>
    ),
    dataIndex: "sat75",
    key: "12",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATEnglish75Percentile ?? "NA"}</div>
    ),
  },

  {
    title: "Times Ranking",
    dataIndex: "timesRanking",
    key: "13",
    width: 140,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.rankings?.TIMES ? `#${record?.rankings?.TIMES}` : "NA"}
      </div>
    ),
  },
  {
    title: "CWUR Ranking",
    dataIndex: "cwurRanking",
    key: "14",
    width: 140,
    align: "center",
    render: (text, record) => (
      <div>{record?.rankings?.CWUR ? `#${record?.rankings?.CWUR}` : "NA"}</div>
    ),
  },
  {
    title: "% Student Receiving Aid",
    dataIndex: "receiveAid",
    key: "15",
    width: 200,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.percStudentsReceivingAid
          ? `${(parseFloat(record?.percStudentsReceivingAid) * 100).toFixed(
              2
            )}%`
          : "NA"}
      </div>
    ),
  },
  {
    title: "Average Financial Aid Granted Per Year",
    dataIndex: "avgAid",
    key: "16",
    width: 250,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.avgFinancialAid
          ? Utils.numWithCommas(record?.avgFinancialAid)
          : "NA"}
      </div>
    ),
  },
  {
    title: "City Type",
    dataIndex: "cityType",
    key: "17",
    width: 130,
    align: "center",
    render: (text, record) => <div>{record?.CityType?.cityType ?? "NA"}</div>,
  },
  {
    title: "US Region",
    dataIndex: "usRegion",
    key: "18",
    width: 130,
    align: "center",
    render: (text, record) => (
      <div>{record?.country?.name === "USA" ? "Yes" : "No"}</div>
    ),
  },
  {
    title: "Research Output",
    dataIndex: "researchOutput",
    key: "19",
    width: 150,
    align: "center",
    render: (text, record) => <div>{record?.researchOutput ?? "NA"}</div>,
  },
];
const defaultCheckedList = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const customIcons = {
  1: <FaDollarSign />,
  2: <FaDollarSign />,
  3: <FaDollarSign />,
  4: <FaDollarSign />,
  5: <FaDollarSign />,
};

const Shortlists = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fixedColumns = [columns[0]];

  const profileData = useSelector((state) => state?.profile?.user);
  const { shortlistSearch } = useSelector((state) => state.search);
  const { watchlists, isLoading, count } = useSelector(
    (state) => state.watchlist
  );

  const [hoveredColIndex, setHoveredColIndex] = useState(null);
  const [checkedList, setCheckedList] = useState(defaultCheckedList);
  const [dragColumns, setDragColumns] = useState(
    columns.slice(1).filter((col) => checkedList.includes(col.key))
  );
  const [budgetMin, setBudgetMin] = useState(null);
  const [budgetMax, setBudgetMax] = useState(null);
  const [pageNo, setPageNo] = useState(shortlistSearch?.pageNo || 1);
  const [filters, setFilters] = useState({
    limit: shortlistSearch?.limit || 10,
    offset: shortlistSearch?.offset || 0,
    search: shortlistSearch?.search || "",
    region: shortlistSearch?.region || [],
    areaOfStudy: shortlistSearch?.areaOfStudy || [],
    majors: shortlistSearch?.majors || [],
    budget: shortlistSearch?.budget || "",
    scholarships: shortlistSearch?.scholarships || "",
    exchangeAvailibility: shortlistSearch?.exchangeAvailibility || "",
    halalFood: shortlistSearch?.halalFood || "",
    locationType: shortlistSearch?.locationType || "",
    tag: shortlistSearch?.tag || [],
  });

  useEffect(() => {
    setDragColumns(
      columns.slice(1).filter((col) => checkedList.includes(col.key))
    );
  }, [checkedList]);

  useEffect(() => {
    if (budgetMin || budgetMax) {
      handleFilterChange({
        budget: `${budgetMin}-${budgetMax}`,
      });
    }
  }, [budgetMin, budgetMax]);

  useEffect(() => {
    const budget = shortlistSearch?.budget || "";
    const matchingValue = UtilityArrays?.budgetValues.find(
      (item) => item.value === budget
    );

    if (!matchingValue && budget) {
      const [min, max] = budget.split("-").map(Number);
      setBudgetMin(min || null);
      setBudgetMax(max || null);
    } else {
      setBudgetMin(null);
      setBudgetMax(null);
    }
  }, [shortlistSearch]);

  useEffect(() => {
    handleSearch();
  }, [shortlistSearch]);

  useEffect(() => {
    dispatch(
      SearchAction.shortlistSearch({
        ...filters,
        pageNo,
      })
    );
  }, [filters, pageNo]);

  useEffect(() => {
    dispatch(
      OthersAction.getPrograms({
        programTypes: JSON.stringify(filters.areaOfStudy),
      })
    );
  }, [filters?.areaOfStudy]);

  const handleSearch = async () => {
    const payload = {
      ...shortlistSearch,
      region: JSON.stringify(shortlistSearch.region),
      majors: JSON.stringify(shortlistSearch.majors),
      areaOfStudy: JSON.stringify(shortlistSearch.areaOfStudy),
      tag: JSON.stringify(shortlistSearch.tag),
    };
    dispatch(WatchlistAction.getWatchlist(payload));
  };

  const onShowSizeChange = (current, pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      limit: pageSize,
      offset: (current - 1) * pageSize,
    }));
    setPageNo(current);
  };

  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    setFilters((prevFilters) => ({
      ...prevFilters,
      offset: (pageNumber - 1) * prevFilters.limit,
    }));
  };

  const handleReset = async () => {
    setFilters({
      ...filters,
      offset: 0,
      search: "",
      region: [],
      areaOfStudy: [],
      majors: [],
      budget: "",
      scholarships: "",
      exchangeAvailibility: "",
      halalFood: "",
      locationType: "",
      tag: [],
    });
    setBudgetMin(null);
    setBudgetMax(null);
    setPageNo(1);
  };

  const handleFilterChange = (updatedFilter) => {
    setPageNo(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilter,
      offset: 0,
    }));
  };
  const handleRemoveFilter = (filterKey, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (Array.isArray(updatedFilters[filterKey])) {
        updatedFilters[filterKey] = updatedFilters[filterKey].filter(
          (item) => item !== value
        );
      } else {
        updatedFilters[filterKey] = "";
      }
      return updatedFilters;
    });
    setPageNo(1);
  };

  const fixedCount = 2;

  const dragProps = {
    onDragEnd(fromIndex, toIndex) {
      const from = fromIndex - fixedCount;
      const to = toIndex - fixedCount;

      if (from < 0 || to < 0) return;

      const newOrder = [...dragColumns];
      const movedItem = newOrder.splice(from, 1)[0];
      newOrder.splice(to, 0, movedItem);
      setDragColumns(newOrder);
    },
    nodeSelector: "th",
    handleSelector: ".dragHandler",
  };

  const finalColumns = [
    {
      title: "#",
      dataIndex: "serial",
      key: "01",
      fixed: "left",
      width: 70,
      render: (text, record, index) => (
        <div>{(pageNo - 1) * filters.limit + index + 1}</div>
      ),
    },
    ...fixedColumns,
    ...dragColumns.map((col, index) => ({
      ...col,
      title: (
        <div className="relative">
          <span
            className="dragHandler"
            style={{ cursor: "move", userSelect: "none" }}
          >
            {col.title}
          </span>

          <popover.WhitePopover
            content={
              <checkbox.VerticalCheckbox
                value={checkedList}
                options={columns.slice(2).map(({ title, key }) => ({
                  label: title,
                  value: key,
                }))}
                onChange={(val) => setCheckedList(val)}
              />
            }
          >
            {hoveredColIndex === index && (
              <FaCirclePlus
                className="absolute -right-3 z-50 top-1/2 -translate-y-1/2 text-white h-[1.75em] w-[1.75em] cursor-pointer"
                title="Add column"
              />
            )}
          </popover.WhitePopover>
          {hoveredColIndex === index && (
            <FaCircleMinus
              className="absolute -left-3 z-50 top-1/2 -translate-y-1/2 text-red-300 h-[1.75em] w-[1.75em] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveColumn(col.key);
              }}
              title="Remove column"
            />
          )}
        </div>
      ),

      onHeaderCell: (column) => ({
        width: column.width,
        onMouseEnter: () => setHoveredColIndex(index),
        onMouseLeave: () => setHoveredColIndex(null),
      }),
    })),
  ];

  const handleRemoveColumn = (colKey) => {
    setCheckedList((prev) => prev.filter((key) => key !== colKey));
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="universities relative">
      {profileData?.freeUser && (
        <div className="absolute top-[150px] left-[35%] z-[10]">
          <GetProPopup />
        </div>
      )}
      <div className={`${profileData?.freeUser && "blur pointer-events-none"}`}>
        <div className="flex justify-center gap-2">
          <inputFields.DefaultSearchBox
            onChange={(e) => {
              setPageNo(1);
              setFilters({
                ...filters,
                offset: 0,
                search: e.target.value,
              });
            }}
            value={filters.search}
            onKeyDown={handleKeyDown}
          />
          <div className="w-[7.5em]">
            <Buttons.DefaultButton
              size="medium"
              width="full"
              style={{ height: "100%" }}
              onClick={() => handleSearch()}
            >
              Search
            </Buttons.DefaultButton>
          </div>
        </div>
        <Spacer height={20} />

        <FilterButtons
          handleFilterChange={handleFilterChange}
          filters={filters}
          budgetMin={budgetMin}
          setBudgetMin={setBudgetMin}
          budgetMax={budgetMax}
          setBudgetMax={setBudgetMax}
          handleReset={handleReset}
          handleRemoveFilter={handleRemoveFilter}
        />
        <Spacer height={20} />
        <boxContainer.UniversityBoxContainer>
          <div className="flex justify-between border-b border-b-liteBoxBorder px-4 py-3">
            <div className="flex items-center gap-3 ">
              <div className="font-bold text-[1.25em] pl-2">
                {count.toLocaleString()} Universities
              </div>
            </div>

            <popover.WhitePopover
              content={
                <checkbox.VerticalCheckbox
                  value={checkedList}
                  options={columns.slice(2).map(({ title, key }) => ({
                    label: title,
                    value: key,
                  }))}
                  onChange={(val) => setCheckedList(val)}
                />
              }
            >
              <SortButton title="Click to edit and personalize the displayed data points by adding or removing them as needed." />
            </popover.WhitePopover>
          </div>
          <div className="w-[calc(100vw-20em)] overflow-x-auto max-w-full relative">
            {" "}
            <ReactDragListView.DragColumn {...dragProps}>
              <tables.DefaultTable
                columns={finalColumns}
                data={watchlists}
                borderradius={0}
                rowKey={() => Math.random()}
                loading={isLoading}
                onRow={(record, index) => ({
                  onClick: (event) => {
                    if (event.target.closest(".delete-icon")) return;

                    if (event.ctrlKey) {
                      window.open(`/universities/${record?.id}`, "_blank");
                    } else {
                      navigate(`/universities/${record?.id}`);
                    }
                  },
                  style:
                    profileData?.freeUser && index !== 0
                      ? { filter: "blur(14px)", pointerEvents: "none" }
                      : {},
                })}
              />
            </ReactDragListView.DragColumn>
          </div>
          {!profileData?.freeUser && (
            <div className="flex justify-center items-center p-3">
              <Pagination
                responsive
                hideOnSinglePage={false}
                current={pageNo}
                pageSize={filters.limit}
                total={count}
                showSizeChanger
                pageSizeOptions={UtilityArrays?.pageSizes}
                onShowSizeChange={onShowSizeChange}
                onChange={handlePageChange}
              />
            </div>
          )}
        </boxContainer.UniversityBoxContainer>
      </div>
    </div>
  );
};

export default Shortlists;
